import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ArrowUpDown, Filter } from 'lucide-react'
import Dropdown from '../ui/Dropdown'
import { useWalletHistory } from '../../hooks/api/useWallet'
import { formatTransactionType, formatPaymentMethod, getTransactionAmountInfo } from '../../utils/transactionFormatter'
import { capitalizeFirstLetter } from '../../utils/stringUtils'
import moment from "moment"

// Backend type options
const TYPE_OPTIONS = ['Select', 'COIN_PURCHASE', 'CHAPTER_PURCHASE', 'CHAPTER_SALE', 'REFUND', 'REWARD', 'TRANSFER', 'CONVERSION', 'WITHDRAWAL_REQUEST']
const STATUS_OPTIONS = ['Select', 'PENDING', 'SUCCESSFUL', 'FAILED', 'CANCELLED']

const STATUS_COLORS = {
    COMPLETED: 'text-green-500',
    SUCCESS: 'text-green-500',
    PENDING: 'text-yellow-500',
    FAILED: 'text-red-500',
    CANCELLED: 'text-gray-400',
}

function TransactionSection({ onTransactionClick }) {
    const today = moment()
    const [page, setPage] = useState(1)
    const [isDateOpen, setIsDateOpen] = useState(false)
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
    const [dateFilter, setDateFilter] = useState({
        startDate: today.clone().subtract(30, "days").startOf("day"),
        endDate: today.endOf("day")
    })
    const [draftMonth, setDraftMonth] = useState(moment())
    const [isMonthSelected, setIsMonthSelected] = useState(false)

    const { data, isLoading } = useWalletHistory({ page, endDate: dateFilter.endDate.toISOString(), startDate: dateFilter.startDate.toISOString() })
    const [expanded, setExpanded] = useState(true)
    const [typeFilter, setTypeFilter] = useState('Select')
    const [statusFilter, setStatusFilter] = useState('Select')

    const transactions = data?.transactions || []
    const pagination = data?.pagination || { page: 1, totalPages: 1 }

    const filteredTransactions = transactions.filter(tx => {
        if (typeFilter !== 'Select' && tx.type !== typeFilter) return false
        if (statusFilter !== 'Select' && tx.status !== statusFilter) return false
        return true
    })

    const handleApplyMonth = () => {
        setDateFilter({
            startDate: draftMonth.clone().startOf("month"),
            endDate: draftMonth.endOf("day")
        })
        setIsMonthSelected(true)
        setPage(1)
    }

    const handleResetMonth = () => {
        setDateFilter({
            startDate: today.clone().subtract(30, "days").startOf("day"),
            endDate: today.endOf("day")
        })
        setDraftMonth(moment())
        setIsMonthSelected(false)
        setIsDateOpen(false)
    }

    // Close mobile filters when screen size changes
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setIsMobileFiltersOpen(false)
            }
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    return (
        <div className="bg-customGray rounded-xl mt-4 overflow-hidden">
            {/* Header Section - Responsive */}
            <div className="w-full p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start"
                    >
                        <span className="font-semibold text-sm sm:text-base">Transaction History</span>
                        {expanded ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />}
                    </button>
                    
                    {expanded && (
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            {/* Mobile Filter Button */}
                            <button
                                onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
                                className="flex items-center gap-2 px-3 py-1.5 bg-[#3a3a3a] rounded-lg text-sm sm:hidden"
                            >
                                <Filter className="w-4 h-4" />
                                <span>Filters</span>
                                {(typeFilter !== 'Select' || statusFilter !== 'Select') && (
                                    <span className="bg-yellow-500 text-black rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                        {(typeFilter !== 'Select' ? 1 : 0) + (statusFilter !== 'Select' ? 1 : 0)}
                                    </span>
                                )}
                            </button>

                            {/* Desktop Filters */}
                            <div className="hidden sm:flex items-center gap-3">
                                <div className="relative">
                                    <button
                                        onClick={() => setIsDateOpen(prev => !prev)}
                                        className="flex items-center gap-2 text-xs sm:text-sm text-gray-300 hover:text-white transition px-2 py-1 bg-[#3a3a3a] rounded-lg"
                                    >
                                        <span className="font-medium">
                                            {isMonthSelected ? dateFilter.startDate.format("MMM YYYY") : "Last 30 Days"}
                                        </span>
                                        <ChevronDown
                                            className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform ${isDateOpen ? "rotate-180" : ""}`}
                                        />
                                    </button>

                                    {isDateOpen && (
                                        <div className="absolute right-0 mt-2 w-[260px] sm:w-[280px] bg-[#2a2a2a] border border-[#3a3a3a] rounded-xl shadow-xl p-4 z-50">
                                            <div className="flex items-center justify-between text-sm text-gray-300">
                                                <button
                                                    onClick={() => setDraftMonth(prev => prev.clone().subtract(1, "month"))}
                                                    className="hover:text-white transition p-1"
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                </button>
                                                <span className="font-medium text-sm">
                                                    {draftMonth.format("MMMM YYYY")}
                                                </span>
                                                <button
                                                    onClick={() => setDraftMonth(prev => prev.clone().add(1, "month"))}
                                                    disabled={draftMonth.isSame(moment(), "month")}
                                                    className="hover:text-white disabled:opacity-40 transition p-1"
                                                >
                                                    <ChevronRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="flex items-center justify-between mt-4 gap-2">
                                                <button
                                                    onClick={handleResetMonth}
                                                    className="flex-1 px-3 py-1.5 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded-md text-xs text-white transition"
                                                >
                                                    Reset
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        handleApplyMonth()
                                                        setIsDateOpen(false)
                                                    }}
                                                    className="flex-1 px-3 py-1.5 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded-md text-xs text-white transition"
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Mobile Filters Panel */}
                <AnimatePresence>
                    {expanded && isMobileFiltersOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="mt-3 sm:hidden"
                        >
                            <div className="bg-[#2a2a2a] rounded-lg p-3 space-y-3">
                                {/* Date Filter for Mobile */}
                                <div>
                                    <label className="text-xs text-gray-400 block mb-2">Select Month</label>
                                    <div className="flex items-center justify-between gap-2">
                                        <button
                                            onClick={() => setDraftMonth(prev => prev.clone().subtract(1, "month"))}
                                            className="p-2 bg-[#3a3a3a] rounded-lg"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>
                                        <span className="text-sm font-medium">
                                            {draftMonth.format("MMMM YYYY")}
                                        </span>
                                        <button
                                            onClick={() => setDraftMonth(prev => prev.clone().add(1, "month"))}
                                            disabled={draftMonth.isSame(moment(), "month")}
                                            className="p-2 bg-[#3a3a3a] rounded-lg disabled:opacity-40"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2 mt-3">
                                        <button
                                            onClick={handleResetMonth}
                                            className="flex-1 px-3 py-1.5 bg-[#3a3a3a] rounded-lg text-xs"
                                        >
                                            Reset
                                        </button>
                                        <button
                                            onClick={() => {
                                                handleApplyMonth()
                                                setIsMobileFiltersOpen(false)
                                            }}
                                            className="flex-1 px-3 py-1.5 bg-yellow-500 text-black rounded-lg text-xs font-semibold"
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>

                                {/* Type Filter for Mobile */}
                                <div>
                                    <label className="text-xs  block mb-2">Transaction Type</label>
                                    <select
                                        value={typeFilter}
                                        onChange={(e) => setTypeFilter(e.target.value)}
                                        className="w-full bg-customDark text-white rounded-lg p-2 text-sm"
                                    >
                                        {TYPE_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt === 'Select' ? 'All Types' : opt.replace('_', ' ')}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Status Filter for Mobile */}
                                <div>
                                    <label className="text-xs text-gray-400 block mb-2">Status</label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="w-full bg-[#3a3a3a] text-white rounded-lg p-2 text-sm"
                                    >
                                        {STATUS_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt === 'Select' ? 'All Status' : opt}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {isLoading ? (
                            <div className="p-3 sm:p-4">
                                {[...Array(3)].map((_, idx) => (
                                    <div
                                        key={idx}
                                        className="h-16 bg-[#3a3a3a] rounded-lg mb-2 animate-pulse"
                                    />
                                ))}
                            </div>
                        ) : filteredTransactions.length === 0 ? (
                            <div className="p-8 sm:p-12 text-center">
                                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#3a3a3a] rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                                    <ArrowUpDown className="w-6 h-6 sm:w-8 sm:h-8 text-gray-500" />
                                </div>
                                <p className="text-gray-400 text-sm sm:text-base">No transactions yet!</p>
                            </div>
                        ) : (
                            <>
                                {/* Mobile Card View */}
                                <div className="block md:hidden">
                                    {filteredTransactions.map((tx, idx) => {
                                        const typeInfo = formatTransactionType(tx.type)
                                        const amountInfo = getTransactionAmountInfo(tx)
                                        return (
                                            <motion.div
                                                key={idx}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ delay: idx * 0.05 }}
                                                onClick={() => onTransactionClick(tx)}
                                                className="border-b border-[#3a3a3a] p-4 hover:bg-[#333] cursor-pointer transition-colors"
                                            >
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-lg">{typeInfo.icon}</span>
                                                        <div>
                                                            <p className="text-sm font-medium">{typeInfo.label}</p>
                                                            <p className="text-xs text-gray-400">{tx.transactionId || tx.id}</p>
                                                        </div>
                                                    </div>
                                                    <span className={`text-sm font-medium ${STATUS_COLORS[tx.status]}`}>
                                                        {tx.status === "COMPLETED" ? "Successful" : capitalizeFirstLetter(tx.status)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <p className={`text-base font-bold ${amountInfo.isCoin ? 'text-customGold' : 'text-white'}`}>
                                                            {amountInfo.display}
                                                        </p>
                                                        <p className="text-xs text-gray-400">{formatPaymentMethod(tx.paymentMethod)}</p>
                                                    </div>
                                                    <p className="text-xs text-gray-400">{moment(tx.createdAt || tx.date).format("MMM Do, HH:mm")}</p>
                                                </div>
                                            </motion.div>
                                        )
                                    })}
                                </div>

                                {/* Desktop Table View */}
                                <div className="hidden md:block overflow-x-auto text-white">
                                    <table className="w-full min-w-[800px]">
                                        <thead>
                                            <tr className="text-gray-400 text-xs sm:text-sm border-b border-[#3a3a3a]">
                                                <th className="text-left p-3 sm:p-4 font-normal">ID</th>
                                                <th className="text-left p-3 sm:p-4 font-normal">
                                                    <Dropdown
                                                        label="Type"
                                                        options={TYPE_OPTIONS}
                                                        value={typeFilter}
                                                        onChange={setTypeFilter}
                                                    />
                                                </th>
                                                <th className="text-left p-3 sm:p-4 font-normal">Amount</th>
                                                <th className="text-left p-3 sm:p-4 font-normal">Payment Method</th>
                                                <th className="text-left p-3 sm:p-4 font-normal">Date & Time</th>
                                                <th className="text-left p-3 sm:p-4 font-normal">
                                                    <Dropdown
                                                        label="Status"
                                                        options={STATUS_OPTIONS}
                                                        value={statusFilter}
                                                        onChange={setStatusFilter}
                                                        colorMap={STATUS_COLORS}
                                                    />
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredTransactions.map((tx, idx) => {
                                                const typeInfo = formatTransactionType(tx.type)
                                                const amountInfo = getTransactionAmountInfo(tx)
                                                return (
                                                    <motion.tr
                                                        key={idx}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ delay: idx * 0.05 }}
                                                        onClick={() => onTransactionClick(tx)}
                                                        className="border-b border-[#3a3a3a] hover:bg-[#333] cursor-pointer transition-colors"
                                                    >
                                                        <td className="p-3 sm:p-4 text-xs sm:text-sm max-w-[120px]">
                                                            <p className='w-full line-clamp-1'>{tx.transactionId || tx.id}</p>
                                                        </td>
                                                        <td className="p-3 sm:p-4 text-xs sm:text-sm">
                                                            <div className="flex items-center gap-1">
                                                                <span>{typeInfo.icon}</span>
                                                                <span className="hidden sm:inline">{typeInfo.label}</span>
                                                                <span className="sm:hidden">{typeInfo.label.substring(0, 10)}...</span>
                                                            </div>
                                                        </td>
                                                        <td className="p-3 sm:p-4 text-xs sm:text-sm font-medium">
                                                            {amountInfo.isCoin && <span className="text-customGold">● </span>}
                                                            {amountInfo.display}
                                                        </td>
                                                        <td className="p-3 sm:p-4 text-xs sm:text-sm">{formatPaymentMethod(tx.paymentMethod)}</td>
                                                        <td className="p-3 sm:p-4 text-xs sm:text-sm text-gray-400">
                                                            {moment(tx.createdAt || tx.date).format("MMM Do, HH:mm:ss")}
                                                        </td>
                                                        <td className={`p-3 sm:p-4 text-xs sm:text-sm font-medium ${STATUS_COLORS[tx.status]}`}>
                                                            {tx.status === "COMPLETED" ? "Successful" : capitalizeFirstLetter(tx.status)}
                                                        </td>
                                                    </motion.tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Controls - Responsive */}
                                <div className="flex items-center justify-between gap-2 p-3 sm:p-4 text-xs sm:text-sm text-gray-400 border-t border-[#3a3a3a]">
                                    <button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={pagination.page <= 1}
                                        className="disabled:opacity-50 px-2 py-1 sm:px-3 sm:py-1.5 bg-[#3a3a3a] rounded-lg hover:bg-[#4a4a4a] transition"
                                    >
                                        <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                                    </button>
                                    <span className="text-xs sm:text-sm">
                                        Page {pagination.page} of {pagination.totalPages}
                                    </span>
                                    <button
                                        onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                                        disabled={pagination.page >= pagination.totalPages}
                                        className="disabled:opacity-50 px-2 py-1 sm:px-3 sm:py-1.5 bg-[#3a3a3a] rounded-lg hover:bg-[#4a4a4a] transition"
                                    >
                                        <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default TransactionSection